export const PAGE_TITLE = 'Order Bot — WhatsApp voice-note orders into Tally';

export const hero = {
  eyebrow: 'For FMCG and wholesale distributors',
  title: 'From a WhatsApp order to Tally, in one approval.',
  lead: 'Order Bot transcribes the voice note, matches each item to the distributor’s Tally stock list and puts the order in a review queue. Nothing reaches Tally until a person approves it.',
};

export const isTodo = (value: string): boolean => value.startsWith('TODO');

export const steps = [
  {
    title: 'Order arrives on WhatsApp',
    body: 'A retailer sends a voice note or a text message to the distributor’s WhatsApp Business number — the way they already order.',
    note: 'Photos of handwritten lists are on the roadmap.',
  },
  {
    title: 'AI reads it',
    body: 'Hindi speech-to-text turns the voice note into text, an LLM pulls out items and quantities, and fuzzy matching maps each item to the distributor’s Tally stock list.',
  },
  {
    title: 'Review queue',
    body: 'The retailer confirms the order on WhatsApp, then it lands in the staff queue with every line tagged Auto, Review or Ask. Staff fix what’s flagged and approve.',
  },
  {
    title: 'Sales order in Tally',
    body: 'A small connector on the distributor’s PC picks up approved orders and creates the Sales Order in TallyPrime, with the item prices it brings in from Tally. The retailer then gets “your order is booked” on WhatsApp.',
  },
] as const;

/**
 * Screenshots of the real review queue. To add one: put the image in
 * apps/web/public/landing/ and set `src` to e.g. '/landing/queue-desktop.png'.
 * The section stays hidden while every `src` is a TODO; once one is real, any
 * remaining TODO renders as a marked placeholder frame.
 */
export const screenshots: ReadonlyArray<{
  src: string;
  alt: string;
  caption: string;
  shape: 'wide' | 'tall';
}> = [
  {
    src: 'TODO: /landing/queue-desktop.png',
    alt: 'The review queue on a laptop',
    caption: 'Review queue — every line tagged Auto, Review or Ask',
    shape: 'wide',
  },
  {
    src: 'TODO: /landing/queue-phone.png',
    alt: 'The review queue on a phone',
    caption: 'The same queue on a phone',
    shape: 'tall',
  },
  {
    src: 'TODO: /landing/ask-retailer.png',
    alt: 'Asking the retailer a question from the review queue',
    caption: 'Ask the retailer a question on WhatsApp from the queue',
    shape: 'wide',
  },
];

export const stack = [
  {
    area: 'Web app',
    items: [
      'React 18',
      'TypeScript',
      'Redux Toolkit + RTK Query',
      'Redux-Saga',
      'Ant Design',
      'Vite',
    ],
  },
  {
    area: 'API',
    items: ['NestJS 10', 'MongoDB (Mongoose)', 'BullMQ + Redis', 'JWT auth', 'Swagger', 'pino'],
  },
  {
    area: 'AI service',
    items: [
      'Python + FastAPI',
      'Pydantic v2',
      'Whisper speech-to-text (local or Groq/OpenAI)',
      'Gemini / Groq / OpenAI extraction',
      'rapidfuzz matching',
    ],
  },
  {
    area: 'Tally connector',
    items: ['Node 20, zero-build JS', 'runs on the distributor’s PC', 'Tally XML over HTTP'],
  },
  {
    area: 'Infra',
    items: [
      'Docker Compose',
      'Caddy (automatic HTTPS)',
      'MinIO / S3',
      'GitHub Actions CI/CD',
      'Web Push',
    ],
  },
] as const;

/** Only mechanisms that exist in this repo today — each one maps to code or a test. */
export const reliability = [
  {
    title: 'Human approval, enforced in code',
    body: 'The order state machine only lets a staff member move an order to Approved, and a unit test guards the rule. Nothing reaches Tally without it.',
  },
  {
    title: 'Webhook replies first, works later',
    body: 'The WhatsApp webhook answers 200 straight away and hands the message to a Redis-backed BullMQ queue, so a slow AI call never makes Meta time out and resend.',
  },
  {
    title: 'No duplicate orders',
    body: 'The WhatsApp message id is both the queue job id and a unique database key, so Meta’s retries can’t create a second order.',
  },
  {
    title: 'Signed webhooks',
    body: 'Inbound webhooks are checked against Meta’s HMAC-SHA256 signature with a constant-time comparison.',
  },
  {
    title: 'Failures stay contained',
    body: 'AI calls have timeouts. If transcription, extraction or matching fails, that one order is marked Failed and the queue keeps moving.',
  },
  {
    title: 'Unsure lines go to a person',
    body: 'Every matched line is scored and tagged Auto, Review or Ask, and Approve stays disabled while any line couldn’t be matched. Staff corrections are saved as aliases the matcher checks first.',
  },
  {
    title: 'Tally never faces the internet',
    body: 'The connector only makes outbound calls: it polls for approved orders and keeps them in an on-disk outbox (idempotent, retry-capped), so a Tally or network outage doesn’t lose an order.',
  },
  {
    title: 'Audit trail and tenant isolation',
    body: 'Every state change is logged with who made it. Staff queries are scoped to the distributor id inside the signed login token, so guessing another order’s id doesn’t expose it.',
  },
  {
    title: 'Guard rails around deploys',
    body: 'Config is validated at startup with zod and tokens are redacted from logs. Deploys run only after lint, type-check and tests pass, and end with a health check of the live API.',
  },
] as const;

/**
 * What a distributor asks before trying it. The staff console's Help page shows the same
 * list in the console's language (features/help/faqTranslations.ts): a change here needs
 * the same change there.
 */
export const faq: ReadonlyArray<{ q: string; a: string }> = [
  {
    q: 'Do retailers have to change how they order?',
    a: 'No. They keep sending WhatsApp voice notes or text messages to your number. They get a summary to confirm, then “order confirmed”, and “your order is booked” once it is in Tally.',
  },
  {
    q: 'Which Tally does it work with?',
    a: 'TallyPrime on a Windows PC, with Tally’s connectivity switched on (port 9000). That PC has to be on with TallyPrime open for orders to go in; until it is, approved orders wait safely.',
  },
  {
    q: 'Does anything go into Tally without us checking?',
    a: 'No. Every order waits in the review queue until one of your staff approves it. Lines the AI isn’t sure about are marked Review or Ask.',
  },
  {
    q: 'How does Order Bot know our products?',
    a: 'The connector reads your stock items, units, pack sizes (alternate units) and selling prices from Tally, and keeps them in sync. Items deleted in Tally stop being matched.',
  },
  {
    q: 'A retailer says “2 peti” but Tally counts pieces. What happens?',
    a: 'The pack size from Tally converts it: if 1 Box = 48 Nos, 2 box becomes 96 Nos. Staff see the conversion on every line.',
  },
  {
    q: 'What about prices?',
    a: 'If an item has a standard selling price in Tally, the Sales Order carries it. Without one, the order goes in at zero and the rate is filled in at invoicing; that needs “Allow zero-valued transactions” switched on for the Sales Order voucher type.',
  },
  {
    q: 'Which party gets the order in Tally?',
    a: 'Each retailer’s phone number is linked to its party ledger on the Retailers page, once per retailer.',
  },
  {
    q: 'What if Tally or the internet is down?',
    a: 'The connector keeps approved orders on the PC and posts them when Tally is back; nothing is posted twice. The owner gets an alert if the PC is offline while orders are waiting.',
  },
  {
    q: 'Which languages does it support?',
    a: 'Hindi, English, Telugu, Kannada, Tamil and Bengali, for the WhatsApp messages and for the staff console.',
  },
  {
    q: 'What happens to our data?',
    a: 'It stays with your account. The “Your data” page shows what is stored and can erase a retailer’s data. Tally’s port is never opened to the internet: the connector only calls out.',
  },
  {
    q: 'How do I install it?',
    a: 'Download the connector from the Tally setup page, run it on the PC that has Tally and enter the pairing code. No administrator rights are needed.',
  },
];

/**
 * How a distributor reaches us from the landing page: the closing section's WhatsApp button and
 * the footer. `whatsapp` is the number with its country code, digits only (e.g. '919876543210');
 * while it's a TODO the page shows a marked placeholder instead of a link.
 */
export const contact = {
  whatsapp: 'TODO: 919643139951',
};

/** A wa.me link that opens a chat with `number`, `text` already typed in. */
export const whatsappLink = (number: string, text: string): string =>
  `https://wa.me/${number.replace(/\D/g, '')}?text=${encodeURIComponent(text)}`;

export const builder = {
  name: 'Sandeep Kumar Sharma',
  blurb:
    'Full-stack engineer (React, Node/NestJS) building AI workflows for real businesses. I built Order Bot end to end: WhatsApp webhooks, the LLM pipeline, the review queue and the Tally connector.',
  github: 'https://github.com/sandeepanshu/',
  linkedin: 'https://www.linkedin.com/in/sandeepanshu/',
};
